<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue';
    import useShow from '@/shared/composables/useShow';
    import { getDocumentationTopicLink } from '@/shared/helpers/routing';
    import { shortDate } from '@/formatters';
    import { downloadFile } from './download';
    import { DocumentFile, getDocumentFiles } from './data';
    import DocumentationBrowserContainer from './DocumentationBrowserContainer.vue';

    const { showError } = useShow();

    const documentFiles = ref<DocumentFile[]>([]);
    const loading = ref<boolean>(false);

    const props = defineProps<{
        identity: string;
    }>();

    const topicName = computed(() => documentFiles.value.length > 0 ? documentFiles.value[0].topicName : '');
    const documentName = computed(() => documentFiles.value.length > 0 ? documentFiles.value[0].documentName : '');
    const topicIdentity = computed(() => documentFiles.value.length > 0 ? documentFiles.value[0].topicIdentity : null);

    onMounted(async () => {
        try {
            loading.value = true;
            documentFiles.value = await getDocumentFiles(props.identity);
        } finally {
            loading.value = false;
        }
    });

    function fileIcon(name: string): string {
        const fileEnding = name.toLowerCase().split('.').pop();
        switch (fileEnding) {
            case 'docx': return 'file-word-outline';
            case 'xlsx': return 'file-excel-outline';
            case 'pptx': return 'file-powerpoint-outline';
            case 'png':
            case 'jpg':
            case 'gif':
                return 'file-image-outline';
            default: return 'file-outline';
        }
    }

    async function download(key: string | null): Promise<void> {
        if (key === null) {
            showError('Attachment missing');
            return;
        }

        try {
            await downloadFile(key);
        } catch (error) {
            showError('Could not download attachment');
        }
    }
</script>

<template>
    <documentation-browser-container>
        <div class="block title is-5">
            <router-link :to="{ name: 'DocumentationBrowser' }">
                Topics
            </router-link>
            <span class="has-text-grey"> / </span>
            <router-link
                v-if="topicIdentity"
                :to="getDocumentationTopicLink(topicIdentity)">
                {{ topicName }}
            </router-link>
            <span class="has-text-grey"> / </span>{{ documentName }}
        </div>
        <b-table
            :data="documentFiles"
            :loading="loading"
            striped>
            <b-table-column
                v-slot="tableProps"
                field=""
                label="">
                <b-icon :icon="fileIcon(tableProps.row.name)" />
            </b-table-column>
            <b-table-column
                v-slot="tableProps"
                field="name"
                label="Name"
                sortable>
                <a @click="download(tableProps.row.attachmentKey)">
                    {{ tableProps.row.name }}
                </a>
            </b-table-column>
            <b-table-column
                v-slot="tableProps"
                field="revision"
                label="Revision"
                sortable>
                {{ tableProps.row.revision }}
            </b-table-column>
            <b-table-column
                v-slot="tableProps"
                field="description"
                label="Description"
                sortable>
                {{ tableProps.row.description }}
            </b-table-column>
            <b-table-column
                v-slot="tableProps"
                field="changes"
                label="Changes"
                sortable>
                {{ tableProps.row.changes }}
            </b-table-column>
            <b-table-column
                v-slot="tableProps"
                field="issuedFor"
                label="Issued for"
                sortable>
                {{ tableProps.row.issuedFor }}
            </b-table-column>
            <b-table-column
                v-slot="tableProps"
                field="dateCreated"
                label="Created"
                sortable>
                {{ shortDate(tableProps.row.dateCreated) }}
            </b-table-column>
            <b-table-column
                v-slot="tableProps"
                field="dateUpdated"
                label="Updated"
                sortable>
                {{ shortDate(tableProps.row.dateUpdated) }}
            </b-table-column>
        </b-table>
    </documentation-browser-container>
</template>
